import {
  WORKING_UNIT_PRESSURE,
  WORKING_UNIT_THICKNESS,
} from 'features/indicationTriage/cards/ffsCalc/ffsUnits'
import isNil from 'lodash/isNil'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

// - - - - - - - - - - - - - - - - - Piping MAWP / Thickness Calculation - - - - - - - - - - - - - - - - -

export type ComputePipeSectionMawpProps = {
  t: GeckoMeasurement | null // effective thickness
  tsl: GeckoMeasurement | null // longitudinal stress
  yieldStrength: GeckoMeasurement | null // S
  qualityFactor: GeckoMeasurement | null // E
  outerDiameter: GeckoMeasurement | null // D
  pressureCoefficient: GeckoMeasurement | null // Y
  weldStrengthReduction?: GeckoMeasurement | null // W
  axis: 'x' | 'y'
}
// Straight Piping Calculation - API 579 2C.5.3

export function computePipeSectionMAWP({
  t,
  tsl,
  yieldStrength,
  qualityFactor,
  outerDiameter,
  pressureCoefficient,
  weldStrengthReduction,
  axis,
}: ComputePipeSectionMawpProps): GeckoMeasurement | null {
  if (
    isNil(t) ||
    isNil(yieldStrength) ||
    isNil(qualityFactor) ||
    isNil(outerDiameter) ||
    isNil(pressureCoefficient)
  ) {
    return null
  }

  const tNum = t.toNumber(WORKING_UNIT_THICKNESS)
  const sNum = yieldStrength.toNumber(WORKING_UNIT_PRESSURE)
  const eNum = qualityFactor.toNumber(GeckoMeasurementUnit.Ratio)
  const dNum = outerDiameter.toNumber(WORKING_UNIT_THICKNESS)
  const yNum = pressureCoefficient.toNumber(GeckoMeasurementUnit.Ratio)
  const wNum = weldStrengthReduction?.toNumber(GeckoMeasurementUnit.Ratio) ?? 1

  // Straight Pipe Circumferential MAWP
  if (axis === 'x') {
    const mawpNum = (2 * tNum * sNum * eNum * wNum) / (dNum - 2 * tNum * yNum)
    return GeckoMeasurement.CreateFromValue(mawpNum, WORKING_UNIT_PRESSURE)
  } else {
    // Straight Pipe Longitudinal MAWP
    if (isNil(tsl)) return null
    const tslNum = tsl.toNumber(WORKING_UNIT_THICKNESS)
    const mawpNum =
      (4 * sNum * eNum * wNum * (tNum - tslNum)) /
      (dNum - 4 * yNum * (tNum - tslNum))
    return GeckoMeasurement.CreateFromValue(mawpNum, WORKING_UNIT_PRESSURE)
  }
}

export type ComputePipeSectionThicknessFromMawpProps = {
  mawp: GeckoMeasurement | null
  yieldStrength: GeckoMeasurement | null
  qualityFactor: GeckoMeasurement | null
  outerDiameter: GeckoMeasurement | null
  pressureCoefficient: GeckoMeasurement | null
  weldStrengthReduction?: GeckoMeasurement | null // W
  tsl: GeckoMeasurement | null
  axis: 'x' | 'y'
}
// Inverse function of computePipeSectionMAWP

export function computePipeSectionThicknessFromMAWP({
  mawp,
  yieldStrength,
  qualityFactor,
  outerDiameter,
  pressureCoefficient,
  weldStrengthReduction,
  tsl,
  axis,
}: ComputePipeSectionThicknessFromMawpProps): GeckoMeasurement | null {
  if (
    isNil(mawp) ||
    isNil(yieldStrength) ||
    isNil(qualityFactor) ||
    isNil(outerDiameter) ||
    isNil(pressureCoefficient)
  ) {
    return null
  }

  const mawpNum = mawp.toNumber(WORKING_UNIT_PRESSURE)
  const sNum = yieldStrength.toNumber(WORKING_UNIT_PRESSURE)
  const eNum = qualityFactor.toNumber(GeckoMeasurementUnit.Ratio)
  const dNum = outerDiameter.toNumber(WORKING_UNIT_THICKNESS)
  const yNum = pressureCoefficient.toNumber(GeckoMeasurementUnit.Ratio)
  const wNum = weldStrengthReduction?.toNumber(GeckoMeasurementUnit.Ratio) ?? 1

  if (axis === 'x') {
    const tNum =
      (mawpNum * dNum) / (2 * sNum * eNum * wNum + 2 * mawpNum * yNum)
    return GeckoMeasurement.CreateFromValue(tNum, WORKING_UNIT_THICKNESS)
  } else {
    if (isNil(tsl)) return null

    const tslNum = tsl.toNumber(WORKING_UNIT_THICKNESS)
    const xNum =
      (mawpNum * dNum) / (4 * sNum * eNum * wNum + 4 * mawpNum * yNum)
    const tNum = xNum + tslNum

    return GeckoMeasurement.CreateFromValue(tNum, WORKING_UNIT_THICKNESS)
  }
}
